import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  useTheme
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { EMAIL_SUBJECT_TEST_TICKET_ACTIONS } from './EmailSubjectTemplate.constants';
import type {
  TestEmailDialogProps,
  TestEmailFormValues
} from './EmailSubjectTemplate.types';

const EmailSubjectTestEmailDialog = (props: TestEmailDialogProps) => {
  const { open, onClose, onSend, isSaving = false } = props;
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<TestEmailFormValues>({
    defaultValues: {
      email: '',
      action: 'created'
    },
    mode: 'onSubmit'
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: TestEmailFormValues) => {
    onSend(data);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Test Email</DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box>
            <InputLabel required htmlFor="email-address">
              Email Address
            </InputLabel>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email address is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              }}
              render={({ field }) => (
                <FormControl id="email-address" fullWidth>
                  <TextField
                    {...field}
                    variant="outlined"
                    placeholder="test@example.com"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isSaving}
                    sx={{ marginTop: '0.5rem' }}
                  />
                </FormControl>
              )}
            />
          </Box>

          <Box>
            <InputLabel required htmlFor="ticket-action-radio-group">
              Ticket Action
            </InputLabel>
            <Controller
              control={control}
              name="action"
              rules={{ required: 'Action is required' }}
              render={({ field }) => (
                <RadioGroup
                  name="ticket-action-radio-group"
                  value={field.value}
                  sx={{ pl: 1, gap: 1, marginTop: '0.5rem' }}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                >
                  {EMAIL_SUBJECT_TEST_TICKET_ACTIONS.map((action) => (
                    <FormControlLabel
                      key={action.value}
                      value={action.value}
                      checked={field.value === action.value}
                      control={
                        <Radio
                          sx={{
                            color: errors.action
                              ? theme.vars.palette.error.main
                              : 'inherit',
                            marginTop: theme.spacing(-0.2)
                          }}
                          disabled={isSaving}
                        />
                      }
                      label={action.label}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSaving}
        >
          {isSaving ? 'Sending...' : 'Send Test Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailSubjectTestEmailDialog;
